import { useCallback, useEffect, useState } from "react";
import { VanoCodesContext } from "./VanoCodesContext";
import { fetchAndValidateVanoCodes } from "@/utils/vanoCodes";

/**
 * Fetches the VA-code catalog once, validates it, and holds it in memory for the lifetime of
 * the authenticated session (no localStorage, no fallback copy - a hard refresh always
 * re-fetches). Gates rendering: `children` is not mounted until the catalog is ready, so no
 * descendant can ever read a missing/unvalidated catalog - there is structurally nothing to
 * read from before that point.
 */
export function VanoCodesProvider({ children }) {
  const [state, setState] = useState({ status: "loading", catalog: null, error: null });

  const load = useCallback(() => {
    setState({ status: "loading", catalog: null, error: null });
    fetchAndValidateVanoCodes()
      .then((catalog) => setState({ status: "ready", catalog, error: null }))
      .catch((error) => setState({ status: "error", catalog: null, error }));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (state.status === "loading") {
    return <div className="p-8 text-center text-gray-500">Loading configuration...</div>;
  }

  if (state.status === "error") {
    return (
      <div className="p-8 text-center text-red-600">
        <div className="mb-2 font-bold">Could not load configuration</div>
        <p className="text-sm mb-4">{state.error?.message}</p>
        <button className="px-4 py-2 rounded bg-emerald-600 text-white" onClick={load}>
          Try again
        </button>
      </div>
    );
  }

  // status === "ready" - children never mount before this, so any descendant calling
  // useVanoCodes() is guaranteed a non-null catalog.
  return (
    <VanoCodesContext.Provider value={state.catalog}>{children}</VanoCodesContext.Provider>
  );
}
